import { NavigationBar } from '@travelnest/hui';
import * as React from 'react';
import { Route } from 'react-router';
import { Routes } from 'react-router-dom';
import { ICustomer } from '../../../data/entities/customer';
import { IFinancial } from '../../../data/entities/customer/financial';
import { ISessionState } from '../../../entities/auth/session/reducers';
import { SaveState } from '../../../types';
import { Loader } from '../../components/loader/Loader';
import { NavPanel } from '../../components/nav-panel/NavPanel';
import { SaveButton } from '../../components/save-button/SaveButton';
import { Tag } from '../../components/tag/Tag';
import { BusinessDetailsEdit } from './business/CustomerBusinessDetails';
import { CustomerPasswordEdit } from './password/CustomerPassword';
import { SupportedPaymentTypes } from './payment/CustomerPaymentDetails';
import { PersonalDetailsEdit } from './personal/CustomerPersonalDetails';
import { IValidationResult } from './validation';

interface ICustomerProps {
	customer: ICustomer | null;
	customerTouched: boolean;
	financial: IFinancial;
	financialTouched: boolean;
	authUser: ISessionState;
	loading: boolean;
	saving: boolean;
	saveState: SaveState;
	resettingPassword: { state: SaveState, context: {} | Error };
	route: string;
	validation: IValidationResult[];
	isImpersonating: boolean;
	passwordless: boolean;
	onChange(customer: ICustomer): any;
	onChangeFinancial(financial: IFinancial): any;
	onSubmit(customer: ICustomer): any;
	onSubmitFinancial(financial: IFinancial): any;
	onSubmitAttributes(attrs: { [key: string]: string }): any;
	onPasswordReset(customer: ICustomer, from: string, to: string): any;
}

interface ICustomerState {
	attributes: { [key: string]: string };
}

const warnTag = (errors?: { count: number }, overrideText = '') => {
	const count = errors ? errors.count : 0;
	return count > 0 && <Tag status="warning">{overrideText ? overrideText : `${count} invalid field${count > 1 ? 's' : ''}`}</Tag>;
};

type FinancialKey = keyof IFinancial;

export class CustomerComponent extends React.Component<ICustomerProps, ICustomerState> {
	constructor(props: ICustomerProps) {
		super(props);
		this.onChangeFinancial = this.onChangeFinancial.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onChangeAttribute = this.onChangeAttribute.bind(this);
		this.state = {
			attributes: {}
		};
	}

	public mapTags(validation: IValidationResult[] = []) {
		const invalids = validation.filter((v) => !v.result.valid).map((v) => v.tab);
		return invalids.reduce((all, inv) => {
			if (!inv) return all;
			all[inv] = all[inv] || { count: 0 };
			all[inv].count += 1;
			return all;
		}, {} as { [key: string]: { count: number; } });
	}

	public onChangeAttribute(key: string, value: string) {
		this.setState({ attributes: { ...this.state.attributes, [key]: value } });
	}

	public onChangeFinancial(key: FinancialKey | FinancialKey[], value: IFinancial[FinancialKey] | Array<IFinancial[FinancialKey]>) {
		if (this.props.financial) {
			let updates: Partial<IFinancial> = {};
			const newState = { ...this.state };

			if (typeof key === 'string') {
				updates = { [key]: value };
			} else {
				updates = key.reduce((obj: Partial<IFinancial>, k: string, i: number) => {
					obj[k] = value[i];
					return obj;
				}, {});
			}

			if (Object.keys(updates).includes('payment_method')) {
				let fields: string[] = [];
				if (this.props.financial.payment_method === SupportedPaymentTypes.UK_BANK) {
					fields = ['account_holder_name', 'account_number', 'sort_code'];
				} else if (this.props.financial.payment_method === SupportedPaymentTypes.INTL_BANK) {
					fields = ['account_holder_name', 'iban', 'swift'];
				} else if (this.props.financial.payment_method === SupportedPaymentTypes.NON_IBAN) {
					fields = ['account_holder_name', 'intl_account_number', 'swift'];
				} else {
					fields = ['intl_account_number', 'swift', 'iban', 'swift', 'account_holder_name', 'account_number', 'sort_code'];
				}
				fields.forEach((k) => updates.payment_method === '' ? delete updates[k] : updates[k] = '');
			}
			this.props.onChangeFinancial({ ...this.props.financial, ...updates });
			this.setState({ ...newState });
		}
	}

	public onChange(key: keyof ICustomer, value: ICustomer[keyof ICustomer]) {
		if (this.props.customer) {
			this.props.onChange({ ...this.props.customer, [key]: value });
		}
	}

	public render() {
		document.title = 'Your account | Travelnest';
		const {
			customer,
			customerTouched,
			financial,
			financialTouched,
			authUser,
			loading,
			onSubmit,
			onSubmitFinancial,
			onSubmitAttributes,
			onPasswordReset,
			saving,
			saveState,
			resettingPassword,
			validation,
			isImpersonating,
			passwordless
		} = this.props;

		const {
			attributes = {}
		} = this.state;
		const attrsTouched = !!Object.keys(attributes).length;

		const urlparts = (this.props.route || '').split('/');
		const page = urlparts[urlparts.length - 1];

		const invalidTabs = this.mapTags(validation);
		const allValid = !(invalidTabs.payment || invalidTabs.business);

		const onSave = () => {
			if (customer && customerTouched) {
				onSubmit(customer);
			}
			if (financial && financialTouched) {
				onSubmitFinancial(financial);
			}

			if (Object.keys(attributes).length) onSubmitAttributes(attributes);

			this.setState({
				attributes: {}
			});
		};

		return <>
			<NavigationBar title="My account" />
			<div className="portal-wrapper">
				<div className="property-edit__actions">
					<SaveButton
						saving={saving}
						saveState={saveState}
						disabled={!((customerTouched || financialTouched || attrsTouched) && allValid)}
						buttonLabel={'Save Changes'}
						successMessage="Your changes have been saved."
						errorMessage="There was an issue saving your changes. Check for invalid fields."
						onClick={onSave}
					/>
				</div>
				{loading ? <Loader loading={true} message={'Your account is loading.'} />
					: !customer ?
						<div className="customer-loading">
							<div className="customer-loading__title">There are no customer details to show.</div>
							<div className="customer-loading__subtitle">Try refreshing the page, and contact support if the problem continues.</div>
						</div>
						:
						<div className="customer__content">
							<NavPanel
								selected={page}
								links={(() => {
									const links = [
										{
											key: 'details',
											link: '/account/details',
											text: 'Personal details',
											subtext: 'Complete your profile',
											children: warnTag(invalidTabs.details)
										},
										{
											key: 'business',
											link: '/account/business',
											text: 'Business and payment details',
											subtext: 'Update business and payment details',
											children: warnTag(invalidTabs.business)
										},
										{
											key: 'password-reset',
											link: 'password-reset',
											text: 'Password',
											subtext: 'Change your password'
										}];
									return links;

								})()}
							/>
							<div className="customer__view">
								<Routes>
									<Route
										path="/details"
										element={<PersonalDetailsEdit
											isImpersonating={isImpersonating}
											onChange={this.onChange}
											onChangeFinancial={this.onChangeFinancial}
											onChangeAttribute={this.onChangeAttribute}
											authUser={authUser}
											customer={customer}
											financial={financial}
											validation={validation} />} />
									<Route
										path="/business"
										element={<BusinessDetailsEdit
											isImpersonating={isImpersonating}
											validation={validation}
											onChange={this.onChange}
											customer={customer}
											financial={financial}
											onChangeFinancial={this.onChangeFinancial}
										/>}
									/>
									{!passwordless && <Route
										path="/password-reset"
										element={<CustomerPasswordEdit
											resettingPassword={resettingPassword}
											onChange={this.onChange}
											onPasswordReset={onPasswordReset}
											customer={customer} />}

									/>}
								</Routes>
							</div>
						</div>}
			</div>

		</>;
	}
}
